import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingService } from '../meeting.service';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import {
  DOSSIER_TYPE,
  DownloadSnippetDto,
  MEETING_STATUS,
  MeetingDto,
  Participant,
  Snippet,
  SNIPPET_TYPE,
  UploadSnippetDto,
} from '@/types/meeting.types';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { getConveneMeetingFormObject } from './utils/convene-meeting-form-object';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { MeetingInviteeDialogComponent } from '@/components/shared/meeting-invitee-dialog/meeting-invitee-dialog.component';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { getInvitationFormObject } from '../new-meeting/utils/add-guest-form-object';
import { MeetingWorkflowBlocksService } from '../meeting-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { BehaviorSubject, switchMap } from 'rxjs';
import { fileUtils } from '@/utils/file.utils';
import { Committee } from '@/types';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getPVUploadDialog } from './utils/pv-upload.dialog-object';
import { meetingConveneValidateSchema } from '@/validations/meeting/meeting-convene-validate.validation';
import { COMMITTEE_ENUM, MeetingTransformationService } from '../meeting-transformation.service';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { getExtraitsPvMeetingDrawer } from './utils/extraits-pv-meeting-drawer-object';
import { DataTableAction, DataTableRowActions } from '@/components/data-table-builder/data-table.types';
import { getSnippetPVUploadDialog } from './utils/pv-snippet-upload.dialog-object';

@Component({
  selector: 'sm-meeting-convene',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    StatusComponent,
    BlockBuilderComponent,
    MeetingInviteeDialogComponent,
    DialogComponent,
    DrawerComponent,
  ],
  templateUrl: './meeting-convene.component.html',
  styleUrl: './meeting-convene.component.scss',
})
export class MeetingConveneComponent implements OnInit {
  activatedRoute = inject(ActivatedRoute);
  meetingService = inject(MeetingService);
  meetingWorkflowBlocksService = inject(MeetingWorkflowBlocksService);
  meetingTransformationService = inject(MeetingTransformationService);
  meetingRepository = inject(MeetingRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);

  id = this.activatedRoute.snapshot.params['id'];
  acId = 0;

  status: MEETING_STATUS = MEETING_STATUS.EN_COURS;

  @ViewChild('dialog') dialog!: DialogComponent;

  @ViewChild('dialogPv') dialogComponent!: DialogComponent;
  pvDialogObject?: DialogObject;

  @ViewChild('drawerPvExtraits') drawerPvExtraits!: DrawerComponent;
  pvExtraitsDrawerObject?: DrawerObject;

  @ViewChild('snippetsDialogUpload') snippetsDialogUpload!: DialogComponent;
  snippetsDialogUploadObject?: DialogObject;

  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.convene',
      routerLink: ['/meetings', 'convene'],
    },
  ];

  isPVNull$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  isMeetingSaving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingCancel$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  haveSnippets = false;

  invitedMembers: Participant[] = [];
  plannedDossiers: any[] = [];

  formObject: DynamicForm = getConveneMeetingFormObject({
    store: this.meetingRepository,
    status: this.status,
    plannedDossiers: this.plannedDossiers,
    createAction: () => this.dialog.toggleVisible(),
  });

  inviteeFormObject: DynamicForm = getInvitationFormObject({
    store: this.meetingRepository,
  });

  controlBlocks: DynamicBlocks = this.meetingWorkflowBlocksService.getConveneMeetingBlocks({
    save: () => this.save(),
    status: this.status,
    isPVNull$: this.isPVNull$,
    isSavingLoading: this.isMeetingSaving$,
    downloadPvDraft: () => this.downloadDraftPV(),
    downloadPv: () => this.downloadPV(),
    validate: () => this.validate(),
    openUploadDialog: () => this.dialogComponent.toggleVisible(),
    inspectPV: () => this.meetingTransformationService.inspectPv(),
    isPVSigned: false,
    cancel: undefined,
    loadingCancel: this.loadingCancel$,
  });

  rowActionsFn: (target: any) => DataTableRowActions = (target: any) => {
    this.acId = target.ac.id;
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};

    if (target.doc === null) {
      additionalActions['snippetsActions'] = [
        {
          label: 'meeting.snippets.table.actions.download',
          icon: 'pi pi-download',
          action: () => this.downloadSnippet(target.ac),
        },
        {
          label: 'meeting.snippets.table.actions.upload',
          icon: 'pi pi-upload',
          action: () => this.snippetsDialogUpload.toggleVisible(),
        },
      ];
    } else {
      additionalActions['snippetsActions'] = [
        {
          label: 'meeting.snippets.table.actions.download',
          icon: 'pi pi-download',
          action: () => this.meetingTransformationService.inspectSnippets(target.doc),
        },
      ];
    }

    return {
      additionalActions,
    };
  };

  ngOnInit() {
    this.loadData();

    this.pvDialogObject = getPVUploadDialog({
      store: this.meetingRepository,
      validate: () => this.uploadSignedPV(),
    });

    this.pvExtraitsDrawerObject = getExtraitsPvMeetingDrawer({
      store: this.meetingRepository,
      data: [],
      totalRecords: 0,
      rowActionsFn: this.rowActionsFn,
      comType: '',
    });

    this.snippetsDialogUploadObject = getSnippetPVUploadDialog({
      store: this.meetingRepository,
      validate: () => this.uploadSnippet(),
    });
  }

  loadData() {
    this.meetingService.getOne(this.id).subscribe({
      next: response => {
        this.status = response.status;
        this.haveSnippets = response.haveExtraits ?? false;
        this.isPVNull$.next(!response.processVerbal);
        this.meetingTransformationService.setResponseToDtoOnLoadData(response);
        this.meetingRepository.set('serverState', this.meetingRepository.get('dto'));
        this.meetingRepository.set('comSelected', response.com ? { name: response.com.nom, code: response.com.id } : undefined);
        this.invitedMembers = response.participants;
        this.plannedDossiers = this.meetingTransformationService.mergePlannedDossiers();
        this.formObject = getConveneMeetingFormObject({
          store: this.meetingRepository,
          invitedMembers: this.invitedMembers,
          status: this.status,
          plannedDossiers: this.plannedDossiers,
          createAction: () => this.dialog.toggleVisible(),
        });
        this.controlBlocks = this.meetingWorkflowBlocksService.getConveneMeetingBlocks({
          save: () => this.save(),
          status: this.status,
          isPVNull$: this.isPVNull$,
          isSavingLoading: this.isMeetingSaving$,
          downloadPvDraft: () => this.downloadDraftPV(),
          downloadPv: () => this.downloadPV(),
          validate: () => this.validate(),
          openUploadDialog: () => this.dialogComponent.toggleVisible(),
          inspectPV: () => this.meetingTransformationService.inspectPv(),
          checkPvExtraits: this.haveSnippets ? () => this.drawerPvExtraits.toggleVisible() : undefined,
          isPVSigned: !!response.processVerbal?.document,
          cancel: () => this.cancel(),
          canBeCancelled: response.canBeCancelled,
          loadingCancel: this.loadingCancel$,
        });

        this.pvDialogObject = getPVUploadDialog({
          store: this.meetingRepository,
          validate: () => this.uploadSignedPV(),
        });

        if (this.haveSnippets) {
          this.meetingService.getSnippetsByMeetingId(this.id).subscribe({
            next: snippet => {
              this.pvExtraitsDrawerObject = getExtraitsPvMeetingDrawer({
                comType: response.com.comType.code,
                store: this.meetingRepository,
                data: snippet.content,
                totalRecords: snippet.totalElements,
                rowActionsFn: this.rowActionsFn,
              });
            },
            error: error => {
              console.log(error);
            },
          });
        }
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.loading'),
        });
      },
    });
  }

  save() {
    this.isMeetingSaving$.next(true);
    const formData = this.meetingTransformationService.prepareDtoForSaveOrValidate(this.meetingRepository.get('dto'));
    this.meetingService.updateConvenedMeeting(this.id, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.saving'),
        });
        this.isMeetingSaving$.next(false);
        this.loadData();
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant('meeting.message.error.saving'),
        });
        this.isMeetingSaving$.next(false);
      },
    });
  }

  cancel() {
    this.loadingCancel$.next(true);
    this.meetingService.cancel(this.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.cancelled'),
        });
        this.loadingCancel$.next(false);
        this.router.navigate(['/meetings']);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.cancelled'),
        });
        this.loadingCancel$.next(false);
      },
    });
  }

  downloadDraftPV() {
    this.meetingService.downloadPvDraft(this.id).subscribe((blob: Blob) => {
      fileUtils.triggerBlobDownload(blob, `PV_draft_meeting_${this.id}.pdf`);
    });
  }

  downloadPV() {
    this.meetingService.downloadPvWithMeeting(this.id).subscribe((blob: Blob) => {
      fileUtils.triggerBlobDownload(blob, `PV_reunion${this.id}.pdf`);
    });
  }

  downloadSnippet(ac: any) {
    const _dto = this.meetingRepository.get<Snippet>('downloadSnippetDto');

    if (_dto.extraitType === SNIPPET_TYPE.BY_AC) {
      this.meetingService.downloadSnippet(this.id, ac.id).subscribe((blob: Blob) => {
        fileUtils.triggerBlobDownload(blob, `Extrait_PV_AC${ac.nom}.pdf`);
      });
    } else {
      let dto: DownloadSnippetDto = {
        reunionId: this.id,
        acId: ac.id,
        extraitType: _dto.extraitType,
      };
      if (_dto.type === DOSSIER_TYPE.DAC) {
        dto = {
          ...dto,
          dacId: _dto.dossier.id,
          dacVer: _dto.dossier.version,
        };
      } else if (_dto.type === DOSSIER_TYPE.PPM) {
        dto = {
          ...dto,
          ppmId: _dto.dossier.id,
          ppmVer: _dto.dossier.version,
        };
      } else if (_dto.type === DOSSIER_TYPE.REQUEST) {
        dto = {
          ...dto,
          demandeId: _dto.dossier.id,
          demandeVer: _dto.dossier.version,
        };
      }
      else if (_dto.type === DOSSIER_TYPE.REPORT) {
        dto = {
          ...dto,
          reportId: _dto.dossier.id,
          reportVer: _dto.dossier.version,
        };
      }
      this.meetingService
        .downloadSnippetCncmp({
          ...dto,
        })
        .subscribe((blob: Blob) => {
          fileUtils.triggerBlobDownload(blob, `Extrait_PV_CNCMP_${_dto.type}.pdf`);
        });
    }
  }

  validate() {
    this.meetingTransformationService.origanizeDossiers(this.meetingRepository.get('plannedDossiers'));
    const meetingDto = this.meetingRepository.get<MeetingDto>('dto');
    const result = meetingConveneValidateSchema.safeParse(meetingDto);
    console.log("result",result);
    if (result.success) {
      const formData = this.meetingTransformationService.prepareDtoForSaveOrValidate(meetingDto);
      this.meetingService.validate(this.id, formData).subscribe({
        next: () => {
          this.loadData();
        },
        error: (error: any) => {
          console.error(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.error.message || this.translate.instant('meeting.message.error.validation'),
          });
        },
      });
    } else {
      console.log("validation issues", result.error.issues);
      console.log("flattened errors", result.error.flatten().fieldErrors);
      this.meetingRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  uploadSignedPV() {
    const fileToUpload: File = this.meetingRepository.getNested<File[]>('upload', 'modelPvFile')[0];
    if (!fileToUpload) return;

    this.meetingService
      .uploadSignedPv(this.id, fileToUpload)
      .pipe(switchMap(() => this.meetingService.getOne(this.id)))
      .subscribe({
        next: response => {
          this.meetingRepository.set('response', response);
          this.meetingRepository.set('dto', {
            ...this.meetingRepository.get('dto'),
            status: response.status,
          });

          if (response.status === MEETING_STATUS.CLOTURER) {
            this.validateCompleteTasks();
          }

          this.dialogComponent.toggleVisible();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.uploaded'),
          });

          this.loadData();
        },
      });
  }

  uploadSnippet() {
    const fileToUpload: File = this.meetingRepository.getNested<File[]>('upload', 'snippetFile')[0];
    if (!fileToUpload) return;

    const dto: UploadSnippetDto = this.meetingRepository.get('uploadSnippetDto');

    this.meetingService
      .uploadSnippet(this.id, {
        ...dto,
        file: fileToUpload,
      })
      .pipe(switchMap(() => this.meetingService.getOne(this.id)))
      .subscribe({
        next: response => {
          this.meetingRepository.set('response', response);
          this.meetingRepository.set('dto', {
            ...this.meetingRepository.get('dto'),
            status: response.status,
          });

          if (response.status === MEETING_STATUS.CLOTURER) {
            this.validateCompleteTasks();
          }

          this.snippetsDialogUpload.toggleVisible();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.uploaded'),
          });

          this.loadData();
        },
      });
  }

  validateCompleteTasks() {
    const meetingDto = this.meetingRepository.get<MeetingDto>('dto');
    const comType = this.meetingRepository.getNested<Committee>('response', 'com').comType.code as COMMITTEE_ENUM;

    let validationGroupPpmArrayDto: FormData = new FormData();
    let validationGroupDacArrayDto: FormData = new FormData();
    let validationGroupQuestionsArrayDto: FormData = new FormData();
    let validationGroupReportsArrayDto: FormData = new FormData();


    if (meetingDto.reunionPpms.length > 0) {
      validationGroupPpmArrayDto = this.meetingTransformationService.buildValidationGroupArrayDto(meetingDto, comType, 'PPM');
      if (validationGroupPpmArrayDto.entries().next().done) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Veuillez sélectionner une option pour valider le dossier Ppm',
        });
        return;
      }
    }
    if (meetingDto.reunionDacs.length > 0) {
      validationGroupDacArrayDto = this.meetingTransformationService.buildValidationGroupArrayDto(meetingDto, comType, 'DAC');
      if (validationGroupDacArrayDto.entries().next().done) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: "Veuillez sélectionner une option pour valider le dossier d'appels de concurrence",
        });
        return;
      }
    }

    if (meetingDto.reunionDemandes.length > 0) {
      validationGroupQuestionsArrayDto = this.meetingTransformationService.buildValidationGroupArrayDto(meetingDto, comType, 'ADDITIF');
      if (validationGroupQuestionsArrayDto.entries().next().done) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Veuillez sélectionner une option pour valider les additifs de demandes',
        });
        return;
      }
    }

    if (meetingDto.reunionReports.length > 0) {
      validationGroupReportsArrayDto = this.meetingTransformationService.buildValidationGroupArrayDto(meetingDto, comType, 'REPORT');
      if (validationGroupReportsArrayDto.entries().next().done) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Veuillez sélectionner une option pour valider les additifs de reports',
        });
        return;
      }
    }

    if (meetingDto.reunionPpms.length > 0) {
      this.meetingService.validateGroupPpm(this.id, validationGroupPpmArrayDto).subscribe({
        next: () => {
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.validation'),
          });
        },
      });
    }
    if (meetingDto.reunionDacs.length > 0) {
      this.meetingService.validateGroupDac(this.id, validationGroupDacArrayDto).subscribe({
        next: () => {
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.validation'),
          });
        },
      });
    }

    if (meetingDto.reunionDemandes.length > 0) {
      this.meetingService.validateGroupQuestions(this.id, validationGroupQuestionsArrayDto).subscribe({
        next: () => {
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.validation'),
          });
        },
      });
    }

    if (meetingDto.reunionReports.length > 0) {
      this.meetingService.validateGroupReports(this.id, validationGroupReportsArrayDto).subscribe({
        next: () => {
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.validation'),
          });
        },
      });
    }
  }
}
